import { createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';

import Image from '~/domains/common/components/Image/Image';
import PicksheetLink from '~/domains/contest/domains/common/components/PicksheetLink/PicksheetLink';
import { Routes } from '~/domains/common/constants/routes';
import { extractPlayerSurnames } from '~/domains/common/utils/extractPlayerSurnames';
import { Text, textStyles } from '~/domains/design-system/Text';
import dt from '~/domains/contest/domains/gamecenter/testing.const';
import playerSilhouette from '~/domains/contest/domains/gamecenter/domains/player/assets/player-sillouette.svg';
import type { PropAlert } from '~/domains/contest/domains/services/interfaces/contest-prop-picks';
import useGetActionBannerInfo from '~/domains/contest/domains/entries/hooks/useGetActionBannerInfo';

import type { ScheduledGamecenterEntryProps } from '../../common/components/GamecenterEntriesPageScheduled/GamecenterEntriesPageScheduled';
import GamecenterEntry from '../../common/components/GamecenterEntriesPageScheduled/GamecenterEntry/GamecenterEntry';
import {
  getAnchorSlate,
  getSlatePickState,
} from '../../common/components/GamecenterEntriesPageScheduled/GamecenterEntry/entryPickState';

const useStyles = createStyles((theme, _, getRef) => ({
  slates: {
    display: 'flex',
    gap: theme.other.spacing._8,
    margin: `0 -${theme.other.spacing._16}px -${theme.other.spacing._16}px`,
    padding: `${theme.other.spacing._8}px ${theme.other.spacing._16}px ${theme.other.spacing._16}px`,
    overflow: 'auto',
  },
  slate: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._4,
    alignItems: 'center',
    color: theme.colors.gray2[3],

    '&.isUpcoming': {
      pointerEvents: 'none',
    },

    '&.isSlateMissingPicks': {
      color: theme.colors.orange[3],
    },

    '&.isSlateMissingPicks, &.isInjuredPlayer, &.hasActionRequiredAlert': {
      [`& .${getRef('slatePicksImages')}`]: {
        borderColor: theme.colors.orange[3],
      },
    },

    '&.isSlatePicksheetNotReady': {
      color: theme.colors.gray2[3],

      [`& .${getRef('slatePicksImages')}`]: {
        borderColor: theme.colors.gray2[3],
      },
    },

    '@media (hover: hover)': {
      '&.isInteractive': {
        transition: 'opacity 150ms ease-in-out',
      },
      '&.isInteractive:not(.isUpcoming):hover': {
        opacity: 0.5,
      },
    },
  },
  slateLabel: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    overflow: 'hidden',
  },

  slatePicksImages: {
    ref: getRef('slatePicksImages'),
    height: 64,
    minWidth: 64,
    border: `1px solid ${theme.colors.gray2[1]}`,
    borderRadius: theme.other.spacing._8,
    display: 'flex',
    padding: `${theme.other.spacing._8}px ${theme.other.spacing._16}px`,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.other.spacing._8,

    '&.isUpcoming': {
      opacity: 0.3,
    },
  },
  slatePickPlaceholder: {
    width: 48,
    height: 48,
    position: 'relative',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 4,
      left: 4,
      display: 'block',
      width: 40,
      height: 40,
      backgroundColor: theme.colors.gray2[1],
      borderRadius: '50%',
    },
  },
  slatePicksNames: {
    marginTop: theme.other.spacing._4,
    textAlign: 'center',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: theme.colors.gray2[7],
    ...textStyles['body-small'],
  },
  injuredPickName: {
    color: theme.colors.orange[3],
    ...textStyles['subhead-x-small'],
  },
  slatePick: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  makePick: {
    textAlign: 'center',
  },
  picksheetNotReady: {
    textAlign: 'center',
    flexGrow: 1,
    color: theme.colors.gray2[4],
  },
  pickImage: {
    width: 48,
    height: 48,
    position: 'relative',

    '& img': {
      objectFit: 'contain',
    },
  },
}));

type GamecenterPlayerSlate = Omit<ScheduledGamecenterEntryProps['slates'][number], 'picks'> & {
  picks: (
    | (NonNullable<ScheduledGamecenterEntryProps['slates'][number]['picks'][number]> & {
        isInjured?: boolean;
      })
    | null
  )[];
  alerts?: PropAlert;
};

export type GamecenterPlayerEntryProps = Omit<ScheduledGamecenterEntryProps, 'slates'> & {
  slates: GamecenterPlayerSlate[];
};

function EntryScheduledMultiSlate({
  contestId,
  entry,
  openEntryNameEditModal,
}: {
  contestId: string;
  entry: GamecenterPlayerEntryProps;
  openEntryNameEditModal: (entry: { id: string; name: string }) => void;
}) {
  const { classes, cx, theme } = useStyles();
  const { t } = useTranslation('contest');

  const canMakePicks = entry.pickState.kind === 'controller';
  const anchorSlate = getAnchorSlate(entry.slates);
  const isFirstSlatePicksheetReady = entry.slates[0]?.isMakePicks ?? true;
  const isInjuredPlayer = entry.slates.some((slate) => slate.picks.some((pick) => pick?.isInjured));

  const { getActionBannerInfo } = useGetActionBannerInfo();
  const alerts = getActionBannerInfo(entry.slates[0]?.alerts);

  const actionsBanner: ComponentProps<typeof GamecenterEntry>['actionsBanner'] = useMemo(() => {
    if (alerts) {
      return alerts;
    }

    if (!isFirstSlatePicksheetReady) {
      return {
        content: t('gamecenter.player.common.entryScheduled.playerPoolNotReady'),
        color: 'gray',
      };
    }
    if (isInjuredPlayer) {
      return {
        content: t('gamecenter.player.common.entryScheduled.injuredPlayer'),
      };
    }
  }, [alerts, isFirstSlatePicksheetReady, isInjuredPlayer, t]);
  if (!anchorSlate) return null;

  const requiredPicksCount = anchorSlate.requiredPicksCount;

  return (
    <GamecenterEntry
      key={entry.id}
      contestId={contestId}
      entryId={entry.id}
      name={entry.name}
      isMultiOwner={entry.isMultiOwner}
      pickState={entry.pickState}
      entryOrder={entry.entryOrder}
      requiredPicksCount={requiredPicksCount}
      openEntryNameEditModal={openEntryNameEditModal}
      actionsBanner={actionsBanner}
    >
      <div className={classes.slates}>
        {entry.slates.map((slate, slateIndex) => {
          // [sidePadding][pick][gap][pick][gap][pick][sidePadding]
          const width =
            // [pick][gap]
            slate.requiredPicksCount * (48 + theme.other.spacing._8) -
            // remove last gap that is not needed
            theme.other.spacing._8 +
            // [sidePadding]
            theme.other.spacing._16 * 2 +
            2;
          const slatePickState = getSlatePickState(entry.pickState, slate);
          const isSlateMissingPicks =
            slateIndex === 0 &&
            (slatePickState.kind === 'controller'
              ? slatePickState.isMissingPicks
              : slatePickState.kind === 'readOnlyMissing');
          const slateHasHiddenPicks = slatePickState.kind === 'readOnlyHidden';
          const slateRequiredPicksCount = slate.requiredPicksCount;
          const isPartialPicks =
            slate.picks.some((pick) => pick) && slate.picks.some((pick) => !pick);

          const filledSlatePicks = slate.picks.filter(Boolean);

          const hasActionRequiredAlert = slate.alerts?.values.some(
            (alert) => alert.status === 'action_required'
          );

          return (
            <PicksheetLink
              isEnabled={canMakePicks}
              className={cx(classes.slate, {
                isInteractive: canMakePicks,
                // Only the pick controller can act on a missing pick, so the orange nudge is theirs alone.
                isSlateMissingPicks: isSlateMissingPicks && slate.isMakePicks && canMakePicks,
                isInjuredPlayer: slate.picks.some((pick) => pick?.isInjured),
                isUpcoming: slate?.isUpcoming,
                hasActionRequiredAlert,
              })}
              key={slate.id}
              aria-disabled={slate?.isUpcoming}
              href={Routes.gamecenterPicks(contestId, { entryId: entry.id, slateId: slate.id })}
              width={width}
              data-test-id={dt.common.entriesScheduled.entry.slate}
            >
              <Text variant="body-small" className={classes.slateLabel} title={slate.name}>
                {slate?.isUpcoming ? t('gamecenter.player.common.upcomingSlate') : slate.name}
              </Text>
              <div style={{ width }}>
                <div
                  className={cx(classes.slatePicksImages, {
                    isUpcoming: slate?.isUpcoming,
                  })}
                >
                  {(() => {
                    if (isSlateMissingPicks) {
                      if (!slate.isMakePicks) {
                        return null;
                      }

                      return (
                        <Text variant="subhead-small" className={classes.makePick}>
                          {canMakePicks
                            ? t('gamecenter.player.common.selectPlayer__hasPlural', {
                                count: slateRequiredPicksCount,
                              })
                            : t('gamecenter.common.noPicks__hasPlural', {
                                count: slateRequiredPicksCount,
                              })}
                        </Text>
                      );
                    }

                    if (slateHasHiddenPicks) {
                      return (
                        <Text variant="subhead-small" className={classes.makePick}>
                          {t('gamecenter.common.pickSelected__hasPlural', {
                            count: slateRequiredPicksCount,
                          })}
                        </Text>
                      );
                    }

                    return slate.picks.map((pick, index) => {
                      if (pick) {
                        return (
                          <div
                            className={classes.slatePick}
                            key={pick.id}
                            data-test-id={dt.team.entriesScheduled.entry.team}
                          >
                            <div className={classes.pickImage}>
                              <Image
                                src={pick.image}
                                alt={pick.name}
                                fill
                                fallbackImageUrl={playerSilhouette.src}
                              />
                            </div>
                          </div>
                        );
                      }

                      if (isPartialPicks) {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <div className={classes.slatePickPlaceholder} key={index} />
                        );
                      }

                      return null;
                    });
                  })()}
                </div>
                <div className={classes.slatePicksNames}>
                  {filledSlatePicks.map((pick, index) =>
                    pick ? (
                      <span key={pick.id}>
                        <span className={pick.isInjured ? classes.injuredPickName : undefined}>
                          {extractPlayerSurnames(pick.name)}
                        </span>
                        {index === filledSlatePicks.length - 1 ? '' : ', '}
                      </span>
                    ) : null
                  )}
                </div>
              </div>
            </PicksheetLink>
          );
        })}
      </div>
    </GamecenterEntry>
  );
}

export default EntryScheduledMultiSlate;
